import React, { useState, useEffect } from "react";
import axios from 'axios';
import './Stock.css'; // Adjust path to where Bids.css is located

const NewStockForm = ({ mode = 'create', stockId = null, onStockCreated, onCancel, currentUserFullName }) => {
    const userType = localStorage.getItem("usertype");
    const loggedInUserId = localStorage.getItem("id") || localStorage.getItem("user_id");

    const [formData, setFormData] = useState({
        stock_name: '',
        chassis_number: '',
        container_number: '',
        shipment_status: 'Waiting for shipment',
        purchase_date: '',
        bid_win_amount: '0',
        total_auction_cost: '0',
        operation_cost: '0',
        miscellaneous_expense: '0',
        freight: '0',
        total_amount_yen: '0',
        usd_conversion: '0',
        amount_usd: '0',
        sale_price: '0',
        status: 'Started',
        demo_url: '',
        assigned_to_type: 'customize',
        group_id: 0,
        reporter: currentUserFullName || 'Super Admin',
        description: '',
        mode: mode
    });

    const [groupsData, setGroupsData] = useState({ administrators: [], groups: [] });
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [permissions, setPermissions] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // These are the Permission Access checkboxes on the right
    const permissionOptions = [
        "Allow Client to Show Team Members",
        "Allow Client to Show Milestones",
        "Allow Client to Show Tasks",
        "Allow Client to Show Task Attachments",
        "Allow Client to Show Timesheets",
        "Allow Client to Show Task Bugs",
        "Allow Client to Show Task History",
        "Allow Client to Show Calendar",
        "Allow Client to Show Comments",
        "Allow Client to Show Gantt Chart",
        "Allow Client to Show Task Hours",
        "Allow Client to Show Task Tasks Comments",
        "Allow Client to Show Task Tasks Attachments",
        "Allow Client to Show Task Tasks Hours",
        "Allow Client to All Finance Overview",
        "Allow Staff to All Finance Overview"
    ];

    useEffect(() => {
        if (formData.assigned_to_type === 'customize' && groupsData.groups.length === 0) {
            fetchGroups();
        }
    }, [formData.assigned_to_type]);

    useEffect(() => {
        if (mode === 'edit' && stockId) {
            loadStockForEdit(stockId);
        }
    }, [mode, stockId]);

    const fetchGroups = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get('https://skcapi.btcjapan.co/get_groups_with_members.php', {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.success) {
                setGroupsData(res.data);
            }
        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    const loadStockForEdit = async (id) => {
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`https://skcapi.btcjapan.co/get_single_stock.php?id=${id}`, { 
                headers: { Authorization: `Bearer ${token}` } 
            });

            if (res.data.success) {
                const data = res.data.data;
                setFormData({
                    ...data,
                    mode: 'edit'
                });
                
                if(data.group_id) setSelectedGroupId(String(data.group_id));

                const groupsRes = await axios.get('https://skcapi.btcjapan.co/get_groups_with_members.php', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (groupsRes.data.success) {
                    setGroupsData(groupsRes.data);
                }

                if (data.assigned_to_type === 'customize') {
                    const permObj = {};
                    (data.permissions || []).forEach(p => {
                        permObj[p.user_id] = {
                            can_view: Number(p.can_view) === 1,
                            can_edit: Number(p.can_edit) === 1,
                            can_delete: Number(p.can_delete) === 1,
                            is_auto: false
                        };
                    });
                    setPermissions(permObj);
                }
            }
        } catch (err) { 
            console.error(err); 
            alert("Error loading stock data");
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (name === 'assigned_to_type' && value === 'customize') {
            setSelectedGroupId('');
            setPermissions({});
        }
    };

    const handleGroupChange = (e) => {
        const groupId = e.target.value;
        setSelectedGroupId(groupId);
        if (!groupId) { setPermissions({}); return; }

        const group = groupsData.groups.find(g => g.group_id == groupId);
        if (!group) return;

        const newPermissions = {};
        groupsData.administrators.forEach(admin => {
            newPermissions[admin.id] = { can_view: true, can_edit: true, can_delete: true, is_auto: true };
        });
        if (group.leader_id) {
            newPermissions[group.leader_id] = { can_view: true, can_edit: true, can_delete: true, is_auto: true };
        }

        setPermissions(prev => {
            const merged = { ...newPermissions };
            Object.keys(prev).forEach(uid => {
                if (!merged[uid] && !prev[uid].is_auto) { merged[uid] = prev[uid]; }
            });
            return merged;
        });
    };

    const toggleMember = (userId, checked) => {
        if (checked) {
            setPermissions(prev => ({
                ...prev,
                [userId]: { can_view: true, can_edit: false, can_delete: false, is_auto: false }
            }));
        } else {
            setPermissions(prev => {
                const copy = { ...prev };
                if (copy[userId]?.is_auto) return prev;
                delete copy[userId];
                return copy;
            });
        }
    };

    const handlePermissionChange = (userId, permType, checked) => {
        setPermissions(prev => {
            const current = prev[userId] || { can_view: false, can_edit: false, can_delete: false };
            return { ...prev, [userId]: { ...current, [permType]: checked } };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.stock_name || !formData.chassis_number) {
            alert("Stock Name and Chassis Number are required.");
            return;
        }
        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            let permissionsPayload = [];
            if (formData.assigned_to_type === 'customize') {
                if (!selectedGroupId) { alert("Please select a group"); setSaving(false); return; }
                permissionsPayload = Object.entries(permissions).map(([userId, perms]) => ({
                    user_id: parseInt(userId),
                    can_view: perms.can_view ? 1 : 0,
                    can_edit: perms.can_edit ? 1 : 0,
                    can_delete: perms.can_delete ? 1 : 0
                }));
            }

            const payload = { 
                ...formData, 
                group_id: selectedGroupId ? parseInt(selectedGroupId, 10) : 0,
                permissions: permissionsPayload
            };
            
            if (mode === 'edit') payload.id = stockId;

            await axios.post('https://skcapi.btcjapan.co/create_update_stock.php', payload, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
            });

            alert(mode === 'edit' ? 'Stock updated successfully!' : 'Stock created successfully!');
            if (onStockCreated) onStockCreated();
        } catch (err) {
            console.error(err);
            alert("Error saving stock.");
        } finally { setSaving(false); }
    };

    let displayGroups = [];
    if (userType === 'administrator') {
        displayGroups = groupsData.groups;
    } else if (userType === 'leader') {
        displayGroups = groupsData.groups.filter(g => String(g.leader_id) === String(loggedInUserId));
    } else if (userType === 'member') {
        displayGroups = groupsData.groups.filter(group => group.members.some(m => String(m.id) === String(loggedInUserId)));
    }

    const selectedGroup = displayGroups.find(g => g.group_id == selectedGroupId);
    const selectedGroupName = displayGroups.find(g => g.group_id == selectedGroupId);

    useEffect(() => {
        if (selectedGroupName && selectedGroupName.leader_name) {
            setFormData(prev => ({ ...prev, reporter: selectedGroupName.leader_name }));
        }
    }, [selectedGroupName]);

    useEffect(() => {
        if (userType === 'member' && selectedGroup) {
            const isMemberInGroup = selectedGroup.members.some(m => String(m.id) === String(loggedInUserId));
            if (isMemberInGroup && !permissions[loggedInUserId]) {
                setPermissions(prev => ({
                    ...prev,
                    [loggedInUserId]: { can_view: true, can_edit: false, can_delete: false, is_auto: true }
                }));
            }
        }
    }, [selectedGroup, loggedInUserId, userType]);

        return (
        <div className={`new-stock-form-container ${mode === 'edit' ? 'sale-order-drawer-form' : ''}`}>
            <form onSubmit={handleSubmit} className="new-stock-form">
                
                {/* ===== HERO SECTION ===== */}
                <div className="stock-form-hero">
                    <div>
                        <div className="stock-form-eyebrow">
                            <span className="eyebrow-dot"></span>
                            NEW STOCK
                        </div>
                        <h2>Create Stock Entry</h2>
                        <p>Fill in the stock details below to add a new vehicle.</p>
                    </div>
                    <div className="stock-form-hero-actions">
                        <button type="submit" className="btn-blue hero-save-btn">Save Stock</button>
                    </div>
                </div>

                {/* ===== STOCK INFORMATION ===== */}
                <div className="form-section-card">
                    <div className="section-card-heading compact">
                        <div className="section-title-wrap">
                            <div className="section-icon">📦</div>
                            <div>
                                <h3>Stock Information</h3>
                                <p>Provide the vehicle and shipment details.</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="form-fields-grid form-fields-grid-3">
                        <div className="form-group">
                            <label>Stock Name <span>*</span></label>
                            <input type="text" name="stock_name" value={formData.stock_name} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                            <label>Chassis Number <span>*</span></label>
                            <input type="text" name="chassis_number" value={formData.chassis_number} onChange={handleChange} required />
                        </div>
                        <div className="form-group">
                            <label>Container Number</label>
                            <input type="text" name="container_number" value={formData.container_number} onChange={handleChange} />
                        </div>

                        <div className="form-group">
                            <label>Shipment Status</label>
                            <select name="shipment_status" value={formData.shipment_status} onChange={handleChange}>
                                <option>Waiting for shipment</option><option>Shipped</option><option>Surrendered</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Purchase Date <span>*</span></label>
                            <input type="date" name="purchase_date" value={formData.purchase_date} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Status</label>
                            <select name="status" value={formData.status} onChange={handleChange}>
                                <option>Started</option><option>In Progress</option><option>In Hold</option><option>Cancel</option><option>Completed</option>
                            </select>
                        </div>

                        {/* Financials */}
                        <div className="form-group">
                            <label>Bid Win Amount</label>
                            <input type="number" step="0.01" name="bid_win_amount" value={formData.bid_win_amount} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Total Auction Cost</label>
                            <input type="number" step="0.01" name="total_auction_cost" value={formData.total_auction_cost} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Operation Cost</label>
                            <input type="number" step="0.01" name="operation_cost" value={formData.operation_cost} onChange={handleChange} />
                        </div>

                        <div className="form-group">
                            <label>Miscellaneous Expense</label>
                            <input type="number" step="0.01" name="miscellaneous_expense" value={formData.miscellaneous_expense} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Freight</label>
                            <input type="number" step="0.01" name="freight" value={formData.freight} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Total Amount in YEN</label>
                            <input type="number" step="0.01" name="total_amount_yen" value={formData.total_amount_yen} onChange={handleChange} />
                        </div>

                        <div className="form-group">
                            <label>USD Conversion</label>
                            <input type="number" step="0.01" name="usd_conversion" value={formData.usd_conversion} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Amount in USD</label>
                            <input type="number" step="0.01" name="amount_usd" value={formData.amount_usd} onChange={handleChange} />
                        </div>
                        <div className="form-group">
                            <label>Sale Price</label>
                            <input type="number" step="0.01" name="sale_price" value={formData.sale_price} onChange={handleChange} />
                        </div>

                        <div className="form-group">
                            <label>Demo URL</label>
                            <input type="text" name="demo_url" value={formData.demo_url} onChange={handleChange} />
                        </div>
                        
                        <div className="form-group">
                            <label>Description</label>
                            <textarea rows="2" name="description" value={formData.description} onChange={handleChange} />
                        </div>
                    </div>
                </div>

                {/* ===== PERMISSION ACCESS ===== */}
                <div className="form-section-card permission-section-card">
                    <div className="section-card-heading">
                        <div className="section-title-wrap">
                            <div className="section-icon">🔐</div>
                            <div>
                                <h3>Permission Access</h3>
                                <p>Assign this stock to specific users or groups.</p>
                            </div>
                        </div>
                        <div className="assigned-radio-group">
                            {/* 
                            <label className="assigned-radio-option permission-choice">
                                <input type="radio" name="assigned_to_type" value="everyone" checked={formData.assigned_to_type === 'everyone'} onChange={handleChange} />
                                <span><strong>Everyone</strong><small>All users can view</small></span>
                            </label>
                            */}
                            {(userType === 'administrator' || userType === 'leader') && (
                                <label className="assigned-radio-option permission-choice">
                                    <input type="radio" name="assigned_to_type" value="customize" checked={formData.assigned_to_type === 'customize'} onChange={handleChange} />
                                    <span><strong>Customize Permission</strong><small>Assign to group</small></span>
                                </label>
                            )}
                        </div>
                    </div>

                    {formData.assigned_to_type === 'customize' && (
                        <div className="permission-panel">
                            {loading ? <p className="permission-panel-loading">Loading groups...</p> : (
                                <>
                                    <div className="group-select-block">
                                        <label>Select Group <span>*</span></label>
                                        <select value={selectedGroupId} onChange={handleGroupChange}>
                                            <option value="">-- Select a Group --</option>
                                            {displayGroups.map(g => <option key={g.group_id} value={g.group_id}>{g.group_name}</option>)}
                                        </select>
                                        {displayGroups.length === 0 && <p className="group-empty-msg">No groups available for your account.</p>}
                                    </div>

                                    {selectedGroup && (
                                        <div className="permission-scroll">
                                            <div className="role-block">
                                                <p className="role-block-title admin">Administrators <span>(Auto)</span></p>
                                                {groupsData.administrators.map(admin => (
                                                    <div key={admin.id} className="role-card admin">
                                                        <strong>{admin.fname} {admin.lname}</strong>
                                                        <span className="role-card-tag">(Administrator)</span>
                                                        <div className="role-card-perms">✓ Can View &nbsp; ✓ Can Edit &nbsp; ✓ Can Delete</div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="role-block">
                                                <p className="role-block-title leader">Group Leader <span>(Auto)</span></p>
                                                <div className="role-card leader">
                                                    <strong>{selectedGroup.leader_name}</strong>
                                                    <span className="role-card-tag">(Leader)</span>
                                                    <div className="role-card-perms">✓ Can View &nbsp; ✓ Can Edit &nbsp; ✓ Can Delete</div>
                                                </div>
                                            </div>

                                            <div className="role-block">
                                                <p className="role-block-title member">Group Members</p>
                                                {selectedGroup.members.filter(m => m.id != selectedGroup.leader_id).map(member => {
                                                    const isSelected = !!permissions[member.id];
                                                    const isSelf = String(member.id) === String(loggedInUserId) && userType === 'member';
                                                    const isDisabled = userType === 'member' || isSelf;
                                                    return (
                                                        <div key={member.id} className={`member-card ${isSelected ? 'selected' : ''}`}>
                                                            <label className="member-card-label">
                                                                <input type="checkbox" checked={isSelected || isSelf} disabled={isDisabled} onChange={(e) => toggleMember(member.id, e.target.checked)} />
                                                                {member.fname} {member.lname} {isSelf && <span className="member-self-tag">(You)</span>}
                                                                <span className="member-role-tag">({member.usertype})</span>
                                                            </label>
                                                            {isSelected && (
                                                                <div className="member-permissions-row">
                                                                    <label><input type="checkbox" checked={!!permissions[member.id]?.can_view} onChange={(e) => handlePermissionChange(member.id, 'can_view', e.target.checked)} /> Can View</label>
                                                                    <label><input type="checkbox" checked={!!permissions[member.id]?.can_edit} onChange={(e) => handlePermissionChange(member.id, 'can_edit', e.target.checked)} /> Can Edit</label>
                                                                    <label><input type="checkbox" checked={!!permissions[member.id]?.can_delete} onChange={(e) => handlePermissionChange(member.id, 'can_delete', e.target.checked)} /> Can Delete</label>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="form-submit-row">
                    {mode === 'edit' && <button type="button" className="btn-secondary" onClick={onCancel}>Cancel</button>}
                    <button type="submit" className="btn-blue final-save-btn" disabled={saving}>{saving ? 'Saving...' : 'Save Stock'}</button>
                </div>
            </form>
        </div>
    );
};

export default NewStockForm;